// src/pages/builder/ShopItemsPanel.js
//
// SHOP ITEMS — Layout Builder panel. Runs the student shop's catalogue
// (shopItems/{id}). The prices set here are the ones the buyShopItem Cloud
// Function charges; the browser never sends a price.
//
//   * "Add the starter set" writes the 17 drawn items (hats, glasses, wings,
//     pets, frames) that are missing — it never overwrites one you edited.
//   * Each item: name, price (0 = free), On / Hidden. Hiding takes it out of
//     the shop; anyone who already owns it keeps it.
//   * "+ New item" makes one with a picture: ✨ AI (the Image studio's
//     generateImage, on a plain white background, which is then cut out so
//     only the item shows on the avatar) or an uploaded PNG.
//
// Writes only shopItems (admin rule) and pictures under topic-images/shop/.

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { collection, doc, getDocs, limit, query, serverTimestamp, setDoc } from "firebase/firestore";
import { getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { db, functions, storage } from "../../firebase/firebaseConfig";
import { useShopItems } from "../../services/shopService";
import { ItemArt, SEASON_LABELS, SLOT_LABELS, STARTER_ITEMS, inSeason } from "../../components/avatar/wearableArt";
import ShopBulkAdd from "./ShopBulkAdd";
import AvatarWithOutfit from "../../components/avatar/AvatarWithOutfit";
import "./shopItems.css";

export const KINDS = ["hat", "necklace", "wings", "pet", "frame", "pin", "glasses"];
export const KIND_WORD = {
  necklace: "necklace laid flat in a U shape, as if worn around a neck (the ends at the top left and top right)", hat: "hat", glasses: "pair of glasses", wings: "pair of wings (seen from the back, spread out)", pet: "small cute pet animal, sitting, facing forward", frame: "round decorative ring frame (a circle outline, empty in the middle)", pin: "small shiny enamel pin badge, simple bold shape" };

export const slugify = (s) =>
  String(s || "item")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40) || "item";
const stamp = () => new Date().toISOString().replace(/[-:]/g, "").replace(/\..*$/, "").replace("T", "-");

/**
 * Turns the plain white background around an AI picture transparent:
 * flood-fills inward from the edges through near-white pixels, so white
 * INSIDE the item (a wing's highlight) stays. Edge pixels get a soft fade.
 * Resolves a PNG Blob, or null if the browser can't read the picture (CORS).
 */
export const cutOutWhite = (src, size = 512) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        const s = Math.min(1, size / Math.max(img.naturalWidth, img.naturalHeight));
        c.width = Math.round(img.naturalWidth * s);
        c.height = Math.round(img.naturalHeight * s);
        const ctx = c.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, c.width, c.height);
        const d = ctx.getImageData(0, 0, c.width, c.height);
        const { width: W, height: H, data } = d;
        const whiteness = (i) => Math.min(data[i], data[i + 1], data[i + 2]);
        const seen = new Uint8Array(W * H);
        const stack = [];
        for (let x = 0; x < W; x += 1) stack.push(x, (H - 1) * W + x);
        for (let y = 0; y < H; y += 1) stack.push(y * W, y * W + W - 1);
        while (stack.length) {
          const p = stack.pop();
          if (seen[p]) continue;
          const i = p * 4;
          const w = whiteness(i);
          if (w < 200) continue; // reached the item
          seen[p] = 1;
          // fully white -> clear; nearly white (antialiased edge) -> partly clear
          data[i + 3] = w >= 240 ? 0 : Math.round(((240 - w) / 40) * 255);
          const x = p % W;
          const y = (p / W) | 0;
          if (x > 0) stack.push(p - 1);
          if (x < W - 1) stack.push(p + 1);
          if (y > 0) stack.push(p - W);
          if (y < H - 1) stack.push(p + W);
        }
        ctx.putImageData(d, 0, 0);
        c.toBlob((b) => resolve(b), "image/png");
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

export const uploadPng = async (blob, name) => {
  const path = `topic-images/shop/${slugify(name)}-${stamp()}.png`;
  const r = storageRef(storage, path);
  await uploadBytes(r, blob, { contentType: "image/png", cacheControl: "public, max-age=31536000" });
  return { url: await getDownloadURL(r), path };
};

/* ------------------------------------------------------------------ */
/* One catalogue row                                                   */
/* ------------------------------------------------------------------ */

/** Season + on-sale dates for one item. MM-DD repeats every year. */
export const SeasonFields = ({ value, onChange }) => (
  <span className="shp-season">
    <select value={value.season || ""} onChange={(e) => onChange({ ...value, season: e.target.value })} aria-label="Season">
      <option value="">All year</option>
      {Object.entries(SEASON_LABELS).map(([k, l]) => (
        <option key={k} value={k}>
          {l}
        </option>
      ))}
    </select>
    {value.season && (
      <>
        <input placeholder="from MM-DD" value={value.availableFrom || ""} onChange={(e) => onChange({ ...value, availableFrom: e.target.value.trim() })} aria-label="On sale from (MM-DD)" size={9} />
        <input placeholder="until MM-DD" value={value.availableUntil || ""} onChange={(e) => onChange({ ...value, availableUntil: e.target.value.trim() })} aria-label="On sale until (MM-DD)" size={9} />
      </>
    )}
  </span>
);
const DATE_RX = /^(\d{4}-)?\d{2}-\d{2}$/;
export const cleanSeason = (v) => ({
  season: v.season || "",
  availableFrom: v.season && DATE_RX.test(v.availableFrom || "") ? v.availableFrom : "",
  availableUntil: v.season && DATE_RX.test(v.availableUntil || "") ? v.availableUntil : "",
});

/** One item's picture, full size, over the page. Click anywhere or Esc closes it. */
const ArtZoom = ({ item, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return createPortal(
    <div className="shp-zoom" role="dialog" aria-modal="true" aria-label={item.name || "Item picture"} onClick={onClose}>
      <div className="shp-zoom-card">
        <div className="shp-zoom-art">
          <ItemArt item={item} />
        </div>
        <div className="shp-zoom-name">{item.name}</div>
        <div className="shp-zoom-hint">Click anywhere to close</div>
      </div>
    </div>,
    document.body
  );
};

const ItemRow = ({ item, onSave }) => {
  const [name, setName] = useState(item.name || "");
  const [price, setPrice] = useState(String(item.price ?? 0));
  const [season, setSeason] = useState({ season: item.season || "", availableFrom: item.availableFrom || "", availableUntil: item.availableUntil || "" });
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(false);
  useEffect(() => {
    setName(item.name || "");
    setPrice(String(item.price ?? 0));
    setSeason({ season: item.season || "", availableFrom: item.availableFrom || "", availableUntil: item.availableUntil || "" });
  }, [item.name, item.price, item.season, item.availableFrom, item.availableUntil]);
  const seasonDirty = JSON.stringify(cleanSeason(season)) !== JSON.stringify(cleanSeason({ season: item.season, availableFrom: item.availableFrom, availableUntil: item.availableUntil }));
  const dirty = name !== (item.name || "") || Number(price) !== Number(item.price || 0) || seasonDirty;
  const save = async (patch) => {
    setBusy(true);
    try {
      await onSave(item.id, patch);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`shp-row ${item.active === false ? "is-hidden" : ""}`}>
      <button type="button" className={`shp-art shp-art-${item.kind}`} onClick={() => setZoom(true)} title="See it full size" aria-label={`See ${item.name || "this item"} full size`}>
        <ItemArt item={item} />
      </button>
      {zoom && <ArtZoom item={item} onClose={() => setZoom(false)} />}
      <input className="shp-name" value={name} onChange={(e) => setName(e.target.value)} aria-label="Name" />
      <label className="shp-price">
        🪙
        <input type="number" min="0" step="5" value={price} onChange={(e) => setPrice(e.target.value)} aria-label="Price" />
      </label>
      {dirty && (
        <button type="button" className="lb-btn lb-btn-sm" disabled={busy} onClick={() => save({ name: name.trim() || item.name, price: Math.max(0, Math.floor(Number(price) || 0)), ...cleanSeason(season) })}>
          Save
        </button>
      )}
      <button
        type="button"
        className={`lb-btn lb-btn-sm ${item.active === false ? "lb-btn-soft" : "lb-btn-ghost"}`}
        disabled={busy}
        onClick={() => save({ active: item.active === false })}
        title={item.active === false ? "Put it back in the shop" : "Take it out of the shop (owners keep it)"}
      >
        {item.active === false ? "Hidden — show it" : "On sale"}
      </button>
      <SeasonFields value={season} onChange={setSeason} />
      {item.season && <span className={`shp-free ${inSeason(item) ? "" : "is-off"}`}>{inSeason(item) ? "ON SALE NOW" : "off-season"}</span>}
      {Number(item.price) === 0 && <span className="shp-free">FREE</span>}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* New item                                                            */
/* ------------------------------------------------------------------ */

const NewItem = ({ onCreated, sampleAvatar, existingIds }) => {
  const [kind, setKind] = useState("hat");
  const [name, setName] = useState("");
  const [price, setPrice] = useState("50");
  const [idea, setIdea] = useState("");
  const [season, setSeason] = useState({ season: "", availableFrom: "", availableUntil: "" });
  const [pic, setPic] = useState(null); // { url, path, cut }
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);

  const makeAi = async () => {
    setBusy("ai");
    setMsg(null);
    try {
      const prompt = [
        `A single ${KIND_WORD[kind]} for a children's game avatar: ${idea.trim() || name.trim()}.`,
        "Cartoon sticker style: bold dark outlines, flat bright colours, one soft highlight, friendly and cute.",
        "Show ONLY the item — no person, head or body wearing it, no shadow, no text.",
        "Centred, filling most of the square, on a plain pure white background.",
      ].join("\n");
      const res = await httpsCallable(functions, "generateImage", { timeout: 170000 })({
        prompt,
        style: "none",
        model: "standard",
        aspectRatio: "1:1",
        name: `shop-${slugify(name || idea)}`,
        refs: [],
      });
      const url = res?.data?.url;
      if (!url) throw new Error("No picture came back. Try describing it differently.");
      setBusy("cut");
      const png = await cutOutWhite(url);
      if (png) {
        const up = await uploadPng(png, name || idea || kind);
        setPic({ ...up, cut: true });
      } else {
        setPic({ url, path: res.data.path || "", cut: false });
        setMsg({ tone: "warn", text: "Couldn’t cut out the white background (the bucket’s CORS must list this site), so it will show on a white square." });
      }
    } catch (err) {
      setMsg({ tone: "bad", text: err?.message || String(err) });
    } finally {
      setBusy("");
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy("up");
    setMsg(null);
    try {
      // a PNG with see-through background goes up as it is; anything else
      // has its white background cut out first
      let blob = file;
      if (!/png/i.test(file.type)) {
        const local = URL.createObjectURL(file);
        blob = (await cutOutWhite(local)) || file;
        URL.revokeObjectURL(local);
      }
      const up = await uploadPng(blob, name || file.name);
      setPic({ ...up, cut: true });
    } catch (err) {
      setMsg({ tone: "bad", text: `Upload failed: ${err?.message || err}` });
    } finally {
      setBusy("");
    }
  };

  const create = async () => {
    const base = `${kind}-${slugify(name)}`;
    let id = base;
    for (let n = 2; existingIds.has(id); n += 1) id = `${base}-${n}`;
    setBusy("save");
    try {
      await setDoc(doc(db, "shopItems", id), {
        name: name.trim(),
        kind,
        price: Math.max(0, Math.floor(Number(price) || 0)),
        imageUrl: pic.url,
        imagePath: pic.path || "",
        prompt: idea.trim(),
        ...cleanSeason(season),
        active: true,
        sort: 100,
        createdAt: serverTimestamp(),
      });
      onCreated(id);
      setName("");
      setIdea("");
      setPic(null);
      setMsg({ tone: "ok", text: "Added to the shop." });
    } catch (err) {
      setMsg({ tone: "bad", text: err?.message || String(err) });
    } finally {
      setBusy("");
    }
  };

  const preview = pic ? { id: "preview", kind, imageUrl: pic.url } : null;

  return (
    <div className="shp-new">
      <div className="shp-new-form">
        <div className="shp-new-row">
          <label>
            Kind
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {SLOT_LABELS[k].replace(/s$/, "")}
                </option>
              ))}
            </select>
          </label>
          <label className="shp-grow">
            Name
            <input value={name} placeholder="e.g. Astronaut Helmet" onChange={(e) => setName(e.target.value)} />
          </label>
          <label>
            Price
            <input type="number" min="0" step="5" value={price} onChange={(e) => setPrice(e.target.value)} />
          </label>
        </div>
        <label>
          Season (optional — holiday items are only on sale between the dates)
          <SeasonFields value={season} onChange={setSeason} />
        </label>
        <label>
          What it looks like (for AI)
          <textarea rows={2} value={idea} placeholder="e.g. a white astronaut helmet with a gold visor and a little NASA-style patch" onChange={(e) => setIdea(e.target.value)} />
        </label>
        <div className="shp-new-row">
          <button type="button" className="lb-btn lb-btn-sm" disabled={Boolean(busy) || !(idea.trim() || name.trim())} onClick={makeAi}>
            {busy === "ai" ? "Making… (up to a minute)" : busy === "cut" ? "Cutting out…" : "✨ Make with AI"}
          </button>
          <label className={`lb-btn lb-btn-sm lb-btn-soft shp-upload ${busy ? "is-disabled" : ""}`}>
            {busy === "up" ? "Uploading…" : "⬆ Upload a picture"}
            <input type="file" accept="image/png,image/jpeg,image/webp" onChange={onFile} disabled={Boolean(busy)} />
          </label>
          <button type="button" className="lb-btn lb-btn-sm lb-btn-save" disabled={!pic || !name.trim() || Boolean(busy)} onClick={create}>
            {busy === "save" ? "Adding…" : "Add to shop"}
          </button>
        </div>
        {msg && <p className={`shp-msg is-${msg.tone}`}>{msg.text}</p>}
      </div>
      <div className="shp-new-preview">
        {pic ? (
          <>
            <span className={`shp-art shp-art-${kind} shp-art-big`}>
              <ItemArt item={preview} />
            </span>
            <AvatarWithOutfit avatarUrl={sampleAvatar} tryOn={preview} size={96} items={{}} />
          </>
        ) : (
          <span className="shp-new-empty">The picture shows here — on its own and on an avatar.</span>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Panel                                                               */
/* ------------------------------------------------------------------ */

/**
 * The shop-items manager without its card: used inside the Layout Builder's
 * "Rewards & avatar settings" panel (RewardsSettingsPanel.js).
 */
export const ShopItemsManager = () => {
  const open = true;
  const { items, loaded, error } = useShopItems(open);
  const [kind, setKind] = useState("all");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [sampleAvatar, setSampleAvatar] = useState("");
  useEffect(() => {
    if (!open || sampleAvatar) return;
    getDocs(query(collection(db, "avatars"), limit(1)))
      .then((s) => setSampleAvatar(s.docs[0]?.data()?.url || ""))
      .catch(() => {});
  }, [open, sampleAvatar]);

  const ids = useMemo(() => new Set(items.map((i) => i.id)), [items]);
  const missingStarters = STARTER_ITEMS.filter((s) => !ids.has(s.id));
  const shown = items.filter((i) => kind === "all" || i.kind === kind);

  const addStarters = async () => {
    setBusy(true);
    try {
      for (const s of missingStarters) {
        const { id, ...rest } = s;
        // eslint-disable-next-line no-await-in-loop
        await setDoc(doc(db, "shopItems", id), { ...rest, active: true, createdAt: serverTimestamp() });
      }
      setNotice({ tone: "ok", text: `Added ${missingStarters.length} items to the shop.` });
    } catch (err) {
      setNotice({ tone: "bad", text: err?.code === "permission-denied" ? "Permission denied — deploy the firestore.rules with the shopItems block." : err?.message || String(err) });
    } finally {
      setBusy(false);
    }
  };

  // Starter items whose price differs from the current recommended list
  // (wearableArt.js STARTER_ITEMS) — e.g. after the Oct 2026 price raise.
  const repriceable = STARTER_ITEMS.filter((st) => {
    const live = items.find((i) => i.id === st.id);
    return live && Number(live.price) !== Number(st.price);
  });
  const applyRecommendedPrices = async () => {
    if (!window.confirm(`Change ${repriceable.length} starter item price(s) to the recommended prices? Items students already own are not affected.`)) return;
    setBusy(true);
    try {
      for (const st of repriceable) {
        // eslint-disable-next-line no-await-in-loop
        await setDoc(doc(db, "shopItems", st.id), { price: st.price, updatedAt: serverTimestamp() }, { merge: true });
      }
      setNotice({ tone: "ok", text: `Updated ${repriceable.length} prices.` });
    } catch (err) {
      setNotice({ tone: "bad", text: err?.message || String(err) });
    } finally {
      setBusy(false);
    }
  };

  const saveItem = async (id, patch) => {
    try {
      await setDoc(doc(db, "shopItems", id), { ...patch, updatedAt: serverTimestamp() }, { merge: true });
    } catch (err) {
      setNotice({ tone: "bad", text: err?.message || String(err) });
    }
  };

  return (
    <div className="shp-manager">
      <div className="shp-bar">
        {missingStarters.length > 0 && (
          <button type="button" className="lb-btn" disabled={busy} onClick={addStarters}>
            {busy ? "Adding…" : `Add the starter set (${missingStarters.length} items)`}
          </button>
        )}
        {repriceable.length > 0 && (
          <button type="button" className="lb-btn" disabled={busy} onClick={applyRecommendedPrices} title="Hats, wings, pets, frames and power-ups">
            Use recommended prices ({repriceable.length})
          </button>
        )}
        <button type="button" className="lb-btn lb-btn-soft" onClick={() => setShowNew((v) => !v)}>
          {showNew ? "Close new item" : "+ New item"}
        </button>
        <button type="button" className="lb-btn lb-btn-soft" onClick={() => setShowBulk((v) => !v)}>
          {showBulk ? "Close bulk add" : "⇪ Bulk add"}
        </button>
        <span className="shp-seg" role="group" aria-label="Kind">
          {["all", ...KINDS, "powerup"].map((k) => (
            <button key={k} type="button" className={kind === k ? "is-on" : ""} onClick={() => setKind(k)}>
              {k === "all" ? `All ${items.length}` : `${SLOT_LABELS[k]} ${items.filter((i) => i.kind === k).length}`}
            </button>
          ))}
        </span>
      </div>

      {notice && (
        <p className={`shp-msg is-${notice.tone}`}>
          {notice.text}{" "}
          <button type="button" className="shp-x" onClick={() => setNotice(null)}>
            ✕
          </button>
        </p>
      )}

      {showBulk && <ShopBulkAdd existingIds={ids} onDone={() => setShowBulk(false)} />}
      {showNew && <NewItem sampleAvatar={sampleAvatar} existingIds={ids} onCreated={() => setShowNew(false)} />}

      {!loaded ? (
        <p className="shp-tiny">Loading the shop…</p>
      ) : error ? (
        <p className="shp-msg is-bad">Couldn’t read shopItems ({error}). Deploy the firestore.rules with the shopItems block.</p>
      ) : items.length === 0 ? (
        <p className="shp-tiny">The shop is empty. Press “Add the starter set” to stock it.</p>
      ) : (
        <div className="shp-list">
          {shown.map((item) => (
            <ItemRow key={item.id} item={item} onSave={saveItem} />
          ))}
        </div>
      )}
    </div>
  );
};

const ShopItemsPanel = () => {
  const [open, setOpen] = useState(false);
  return (
    <section className="lb-card shp-panel">
      <div className="lb-card-head">
        <span className="lb-step shp-step">🛍</span>
        <div>
          <h2>Shop items</h2>
          <p className="lb-hint">
            What students can spend their coins on: hats, crowns, wings, pets, frames and pins for their avatar. Prices
            set here are what students pay.
          </p>
        </div>
        <button type="button" className="lb-btn" onClick={() => setOpen((v) => !v)}>
          {open ? "Hide" : "Open"}
        </button>
      </div>
      {open && <ShopItemsManager />}
    </section>
  );
};

export default ShopItemsPanel;
